import {
  AskConfirmation,
  buttonsContinueCancel,
  ConfirmationButtonType,
  getConfirmationButtons
} from '../../../webmodule-common/other/ui/modal-confirmation';
import { asMarkdown } from '../../../webmodule-common/other/general/markdown';
import { awaitableTemplate } from '../../../webmodule-common/other/ui/template-processor';
import { constructAsync } from '../../../webmodule-common/other/async-constructor';
import { currentQuoteSupplierProvider } from '../data/quoteSupplierProvider';
import { customElement } from 'lit/decorators.js';
import { DataCacheGeneric } from '../../../webmodule-common/cache/generic-data-cache';
import { DataEntryOwner } from '../../../webmodule-common/other/ui/DataEntryOwner';
import { DataEntryTabManagerView, ModalViewBase } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { DealerReport } from '../../api/dealer-api-interface-clientagent';
import { DevelopmentError, showDevelopmentError } from '../../../webmodule-common/other/development-error';
import { disposeOf } from '../../../webmodule-common/other/dispose';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import {
  EventGetReference,
  EventSnippet,
  EventTemplate,
  NullPromise,
  PromiseTemplate,
  Snippet
} from '../../../webmodule-common/other/ui/events';
import { FileMap } from '../../../webmodule-common/other/api/api-type-base-interface';
import {
  fireQuickErrorToast,
  fireQuickInformationToast,
  fireQuickSuccessToast,
  fireQuickWarningToast
} from '../../../webmodule-common/other/toast-away';
import { FreehandLineItemView } from './freehand-line-item-view';
import { getApiFactory } from '../../api/api-injector';
import { html, nothing, TemplateResult } from 'lit';
import { information } from '../../../webmodule-common/other/ui/modal-option';
import { isAutoSaving } from '../../../webmodule-common/other/save-workflow';
import { isContentTypeSSI, isFrame, isSpecialItem } from '../data/quote-helper-functions';
import { isDebugMode } from '../../../webmodule-common/other/debug';
import { isMultiSupplier } from '../quote-service';
import { isSupplierAgent } from '../../../webmodule-common/other/currentuser-claims';
import { IUserQuoteStateEngine } from '../data/quote-state-engine';
import { LangQuoteView } from '../../../localization/scripts/quotes/views/quote-view-lang';
import { TabManager } from '../../../webmodule-common/other/ui/tab-manager/tab-manager';
import type {
  TabAction,
  TabCloseEvent,
  TabPage
} from '../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { QuoteApi } from '../../api/quote-api';
import { QuoteContainer, QuoteContainerManager } from '../data/quote-container';
import { QuoteDetailView, QuoteDetailViewOptions } from './quote-detail-view';
import { quoteItemAction } from '../data/events';
import { QuoteItemContainer } from '../data/quote-item-container';
import { quoteItemContentType } from '../data/default-quote-item-content-type';
import { QuoteItemsView as QuoteItemsView, QuoteItemsViewOptions } from './quote-items-view';
import { QuoteItemView, QuoteItemViewOptions } from './quote-item-view';
import { QuoteItem, QuoteState } from '../../api/dealer-api-interface-quote';
import { QuoteSupplierDefaultsView, QuoteSupplierDefaultsViewOptions } from './quote-supplier-defaults.view';
import { QuoteSupplierGlassView, QuoteSupplierGlassViewOptions } from './quote-supplier-glass-view';
import { ResultGetSupplierQuoteSettings } from '../../api/dealer-api-interface-franchisee';
import { ShippingLineItemView } from './shipping-line-item-view';
import { showError } from '../../../webmodule-common/other/ui/show-error';
import { SpecialLineItemView } from './special-line-item-view';
import { SSEQuote } from '../data/sse-quote';
import { SupplierApi } from '../../api/supplier-api';
import { supplierQuoteItemContentType } from '../data/supplier-quote-item-content-type';
import { waitPatientlyLoading } from '../../../webmodule-common/other/ui/modal-loading';
import { WMEventSource } from '../../api/event-source';
import { lockUIandExecute } from '../../../webmodule-common/other/ui-lock';
import { glossary, interpolateExternal } from '../../../localization/global';
import { vrmlSupport } from '../../../webmodule-common/interop/interfaces/v6-config';
import { noParalellExecutionAsync } from '../../../webmodule-common/other/common/helpers/callbacks';

export interface QuoteViewOptions {
  quoteContainerManager: QuoteContainerManager;
  title: EventSnippet;
  customerCache: DataCacheGeneric;
  userCache: DataCacheGeneric;
  ownerId: EventGetReference;
}

export interface ButtonCaptionProvider {
  caption: string;
}

export interface QuoteViewChildFactory {
  getDetailView(options: QuoteDetailViewOptions): QuoteDetailView;

  getItemsView(options: QuoteItemsViewOptions): QuoteItemsView;

  getSupplierDefaultsView(options: QuoteSupplierDefaultsViewOptions): QuoteSupplierDefaultsView;

  getSupplierGlassView(options: QuoteSupplierGlassViewOptions): QuoteSupplierGlassView;

  getSupplierQuoteItemView(
    contentType: number,
    options: QuoteItemViewOptions,
    supplierSelector?: unknown
  ): QuoteItemView;
}

export class QuoteViewChildFactoryImpl implements QuoteViewChildFactory {
  parent: QuoteDataEntryView;

  constructor(parent: QuoteDataEntryView) {
    this.parent = parent;
  }

  getDetailView(options: QuoteDetailViewOptions): QuoteDetailView {
    return new QuoteDetailView(options);
  }

  getItemsView(options: QuoteItemsViewOptions): QuoteItemsView {
    return new QuoteItemsView(options);
  }

  getSupplierDefaultsView(options: QuoteSupplierDefaultsViewOptions): QuoteSupplierDefaultsView {
    return new QuoteSupplierDefaultsView(options);
  }

  getSupplierGlassView(options: QuoteSupplierGlassViewOptions): QuoteSupplierGlassView {
    return new QuoteSupplierGlassView(options);
  }

  getSupplierQuoteItemView(
    _contentType: number,
    options: QuoteItemViewOptions,
    _supplierSelector?: unknown
  ): QuoteItemView {
    return new QuoteItemView(options);
  }
}

//Quote view encloses the entire Quote edit and view process
// contains detail view
// will contained default settings view
// quote items list.

@customElement('wm-quotedataentryview')
export class QuoteDataEntryView extends DataEntryTabManagerView {
  quoteApi: QuoteApi = getApiFactory().quote();
  supplierApi: SupplierApi = getApiFactory().supplier();
  initPromises: Promise<any>[] = [];
  ownerId: EventGetReference;
  public userProfileCache: DataCacheGeneric;
  supplierQuoteConfig?: ResultGetSupplierQuoteSettings;
  protected eventTitle: EventSnippet;
  protected blobApi = getApiFactory().blob();
  protected clientApi = getApiFactory().client();
  protected quoteViewChildFactory: QuoteViewChildFactory;
  protected detailView: QuoteDetailView;

  protected itemsView: QuoteItemsView;

  protected supplierQuoteDefaultsView: QuoteSupplierDefaultsView;
  protected readonly maxItemPageAllowed: number = 1;
  protected supplierGlassView: QuoteSupplierGlassView | null;
  private lastDeletedTabItemId?: string;
  private readonly discardChangesText = LangQuoteView.discardChanges();

  constructor(options: QuoteViewOptions, owner: DataEntryOwner) {
    super(owner);
    this.quoteViewChildFactory = this.getQuoteViewChildFactory();
    this._quoteContainerManager = options.quoteContainerManager;
    this.eventTitle = options.title;
    this.userProfileCache = options.userCache;
    this.ownerId = options.ownerId;

    //UI only created after building quoteContainerManager
    this.detailView = this.quoteViewChildFactory.getDetailView({
      quoteManager: this.quoteContainerManager
    });
    this.itemsView = this.quoteViewChildFactory.getItemsView({
      quoteManager: this.quoteContainerManager,
      userProfileCache: this.userProfileCache
    });

    this.supplierQuoteDefaultsView = this.quoteViewChildFactory.getSupplierDefaultsView({
      quoteManager: this.quoteContainerManager,
      areItemsOpen: () => this.itemPagesOpenCount > 0
    });

    this.supplierGlassView = this.hasSupplierGlassView()
      ? this.quoteViewChildFactory.getSupplierGlassView({
          quoteManager: this.quoteContainerManager,
          forceReadonly: () => this.glassViewReadonly()
        })
      : null;

    this.quoteContainerManager.afterSave.push(async () => {
      this.detailView.requestUpdate();
      await this.itemsView.refreshData();
      this.requestUpdate();
    });
  }

  protected _quoteContainerManager: QuoteContainerManager;

  public get quoteContainerManager(): QuoteContainerManager {
    return this._quoteContainerManager;
  }

  public set quoteContainerManager(value: QuoteContainerManager) {
    this._quoteContainerManager = value;
  }

  public get quoteContainer(): QuoteContainer {
    return this.quoteContainerManager.container;
  }

  get quoteItemsViewPageIndex() {
    let pageindex = 0;
    this.tabManager.pages.some((page, index) => {
      const found = page.data === this.itemsView;
      if (found) pageindex = index;
      return found;
    });
    return pageindex;
  }

  protected get itemPagesOpenCount(): number {
    let count = 0;
    this._tabManager?.pages.forEach(page => {
      if (page.data instanceof QuoteItemView) {
        count++;
      }
    });
    return count;
  }

  protected get isLockedFromUse(): boolean {
    return this.quoteContainerManager.isLockedFromUse();
  }

  private get supplierName(): string {
    return this._quoteContainerManager.supplierName;
  }

  async dispose() {
    WMEventSource.getInstance().removeEventListener(WMEventSource.quote, this._eventQuote);
    await super.dispose();
    await disposeOf(this.detailView);
    await disposeOf(this.itemsView);
    //override if needed
  }
  private runQuoteItemActionEvent = async (e: CustomEvent<{ item: QuoteItem; quoteAction: number }>) => {
    const { item, quoteAction } = e.detail;
    await this.runQuoteItemAction(this.quoteContainerManager.quoteItemContainer(item.id), quoteAction);
    e.stopImmediatePropagation();
    e.stopPropagation();
  };
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('wm-quote-item-action', this.runQuoteItemActionEvent);

    setTimeout(async () => {
      if (!this.isDataReadonly())
        await lockUIandExecute(async () => {
          if (await this.mustValidateItems()) {
            await this.validateOutOfDateQuoteItems();
          }
        });
    }, 100);
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('wm-quote-item-action', this.runQuoteItemActionEvent);
  }
  getQuoteStateEngine(): IUserQuoteStateEngine {
    throw new DevelopmentError('Unsupported. please override;');
  }

  /**
   * inherited
   */
  public async afterConstruction() {
    WMEventSource.getInstance().addEventListener(WMEventSource.quote, this._eventQuote);
    await this.quoteContainerManager.needsQuote();

    if (!this.supplierQuoteConfig) {
      const _supplierQuoteConfig = await this.supplierApi.getSupplierQuoteConfig({
        supplierId: this.quoteContainerManager.quote.supplierId
      });
      if (_supplierQuoteConfig) {
        this.supplierQuoteConfig = _supplierQuoteConfig;
      }
    }

    if (this.supplierGlassView) this.initPromises.push(constructAsync(this.supplierGlassView));

    this.initPromises.push(constructAsync(this.supplierQuoteDefaultsView));
    await this.detailView.afterConstruction();
    await this.itemsView.afterConstruction();

    //this will create the page control
    await super.afterConstruction();
    await this.buildQuoteActionMenu();
  }

  async waitInit(): Promise<void> {
    const w = waitPatientlyLoading(() => LangQuoteView.initializingQuoteData());
    try {
      await Promise.allSettled(this.initPromises);
    } finally {
      await w.hideModal();
    }
  }

  async addNewQuoteItemFreehand(): Promise<boolean> {
    if (this.isDataReadonly()) return false;
    if (!(await this.allowPageSwitch())) return false;
    const providerType = '';
    return await this.constructAndLaunchItemView(providerType, quoteItemContentType.freehand);
  }

  async addNewQuoteItemSpecial(supplierSelector?: unknown): Promise<boolean> {
    if (this.isDataReadonly()) return false;
    if (!(await this.allowPageSwitch())) return false;
    const providerType = '';
    return await this.constructAndLaunchItemView(providerType, quoteItemContentType.specialItem, supplierSelector);
  }

  get activePageData(): any {
    return this._tabManager?.activePage?.data;
  }
  /**
   * inherited;
   * @returns
   */
  public async prepareForSave(): Promise<void> {
    await this.waitInit();
    if (this.isDataReadonly()) return;

    //this should not prepare for save, unless it is active. its data could be stale as it loads
    //on entry, we now have cross contaimination where items can edit defaults.
    //we need a data event subscriptions
    if (this.activePageData === this.supplierQuoteDefaultsView) await this.supplierQuoteDefaultsView.prepareForSave();
    await this.itemsView.prepareForSave();
    await this.detailView.prepareForSave();
    //this should not prepare for save, unless it is active. its data could be stale as it loads
    //on entry
    if (this.activePageData === this.supplierGlassView) await this.supplierGlassView?.prepareForSave();
  }

  async performQuoteItemsViewRefresh(): Promise<void> {
    if (this.itemPagesOpenCount == 0) {
      await this.quoteContainerManager.needsQuoteItems(true);
      await this.itemsView.refreshData();
      fireQuickSuccessToast(LangQuoteView.quoteItemRefreshed());
    } else fireQuickWarningToast(LangQuoteView.closeAllQuoteItemPagesBeforeRefresh());
  }

  createQuoteItemSummaryPage(): TabPage {
    return {
      caption: () => LangQuoteView.quoteItemPlural(),
      canClose: () => false,
      canLeave: async () => await this.allowPageSwitch(),
      onEnter: async () => {
        await this.itemsView.invalidate();
      },
      onAfterEnter: async () => {
        let id: string | undefined = undefined;

        if (this.lastDeletedTabItemId && this.lastDeletedTabItemId != emptyGuid) {
          // if this is set, then the tab was delete (on delete event) and we need to clear it.
          id = this.lastDeletedTabItemId;
          this.lastDeletedTabItemId = undefined;
        }

        if (!id) {
          const editor = await this.findLastAvailableOpenQuoteItemView();

          if (editor) id = (editor.data as QuoteItemView).quoteItemContainer?.item.id;
        }

        if (id) this.dispatchCustom('!wm-scroll-into-view', { selector: `div[data-rowid='${id}']` });
      },
      content: () => {
        return this.itemsView.ui;
      },
      secondaryButtons: () => {
        const vrml =
          vrmlSupport() && this.quoteContainerManager.is3dModelEnabled
            ? html`<webmodule-button size="small" @click=${() => this.showVrml()} variant="default">
                ${LangQuoteView.showVrmlModel()}
              </webmodule-button>`
            : nothing;

        return html`
          ${this.abandonAndCloseButton()}
          <webmodule-button size="small" @click=${() => this.performQuoteItemsViewRefresh()} variant="default">
            ${LangQuoteView.refreshList()}
          </webmodule-button>
          ${awaitableTemplate(this.validateOutOfDateQuoteItemsButton())}${awaitableTemplate(
            this.updatePriceButton()
          )}${vrml}
          ${this.itemsView.renderSettingsMenu(() => this.requestUpdate())}
          ${this.itemsView.renderPricingSummaryButton()}
        `;
      },
      fragment: 'quote-items',
      data: this.itemsView
    };
  }
  showVrml() {
    //do nothing
  }

  async validateOutOfDateQuoteItemsButton(): PromiseTemplate {
    if (!(await this.mustValidateItems())) return html``;

    return html` <webmodule-button
      size="small"
      @click=${async () => await this.validateOutOfDateQuoteItems()}
      variant="default"
    >
      ${LangQuoteView.validate()}
    </webmodule-button>`;
  }

  /**
   * "Update Price" affordance for the deferred-SSI flow (Task 230006, §6): a batch change parked the
   * supplier-item/pricing recompute, so the SSI lines render as "unavailable" until this is run. Clicking
   * re-runs SSI + reprice, clears the flag, and refreshes.
   *
   * Gated on {@link pricingNeedsUpdate}, the SAME predicate that blocks the actions - not on the raw
   * SsiDeferred flag. That predicate also requires the quote to be unlocked and in a state where
   * validation means anything, so a locked quote no longer offers a button that would refuse to do
   * anything if pressed. This is exactly how the Validate button behaves: the affordance and the block
   * are one decision, made once.
   */
  async updatePriceButton(): PromiseTemplate {
    if (!(await this.pricingNeedsUpdate())) return html``;

    return html` <webmodule-button
      size="small"
      variant="primary"
      @click=${async () => {
        await this.quoteContainerManager.updatePrice();
        await this.performQuoteItemsViewRefresh();
        // performQuoteItemsViewRefresh reloads the ITEMS view; it does not re-render this one. The action menu
        // is rebuilt in bodyTemplate, so without this the Reports menu stays disabled and this button stays on
        // screen after the very run that cleared the deferred flag.
        this.requestUpdate();
      }}
    >
      ${LangQuoteView.updatePrice()}
    </webmodule-button>`;
  }

  /**
   *
   * @returns override this to add logic
   */
  async mustValidateItems(): Promise<boolean> {
    return false;
  }

  /**
   * override this to add logic. True when pricing is stale (deferred SSI) and must be refreshed with
   * Update Price before the quote can be acted on.
   */
  async pricingNeedsUpdate(): Promise<boolean> {
    return false;
  }

  /**
   * Blocks an action on stale pricing and says so, naming the button that fixes it. Every action that
   * refuses to run on out-of-date items must refuse on out-of-date pricing too — the two used to share one
   * predicate, so splitting them means each caller now has to ask both questions.
   */
  protected async blockedByStalePricing(): Promise<boolean> {
    if (!(await this.pricingNeedsUpdate())) return false;

    await information(asMarkdown(LangQuoteView.pricingOutOfDateForAction()), LangQuoteView.pricingUpdateRequired());
    return true;
  }

  /**
   * override this to add logic
   */
  async validateOutOfDateQuoteItems() {
    throw new Error('Method not implemented.');
  }

  async getQuoteItemViewOptions(quoteItemContainer: QuoteItemContainer | null): Promise<QuoteItemViewOptions> {
    if (!this.quoteContainer.quote) {
      throw new DevelopmentError('quote-view, quote is null');
    }
    return {
      supplierId: this.quoteContainer.quote.supplierId,
      quoteManager: this.quoteContainerManager,
      quoteItemContainer: quoteItemContainer,
      isClientFacing: () => this.itemsView.isClientFacing
    };
  }

  /**
   * inherited;
   * @returns
   */
  public internalDataChanged(): boolean {
    return !this.abandoned && this.quoteContainerManager.quoteChanged();
  }

  /**
   * inherited
   * @returns
   */
  public getDataDictionaryName(): string {
    return glossary('quote');
  }

  /**
   * inherited
   * @returns
   */
  public isDataReadonly(): boolean {
    return this.quoteContainerManager.isReadonly();
  }

  public getTitle(): Snippet {
    const isMulti = isMultiSupplier();
    const supplierName = this.supplierName;

    return isMulti
      ? html`
          ${this.eventTitle(this.quoteContainer.quote, this.quoteContainerManager.quoteState)}
          <br />
          <span class="subtitle">${glossary('supplier')} ${supplierName}</span>
        `
      : this.eventTitle(this.quoteContainer.quote, this.quoteContainerManager.quoteState);
  }

  public runQuoteItemAction = noParalellExecutionAsync(
    async (quoteItemContainer: QuoteItemContainer, action: number): Promise<void> => {
      //add action switch
      switch (action) {
        case quoteItemAction.edit:
          await this.internalOpenQuoteItemForEdit(quoteItemContainer);
          break;
        case quoteItemAction.properties:
          await this.internalOpenQuoteItemPropertyDialog(quoteItemContainer);
          break;
        case quoteItemAction.copy:
          await this.quoteContainerManager.copyQuoteItem(quoteItemContainer);
          break;
        case quoteItemAction.delete:
          await this.internalDeleteQuoteItem(quoteItemContainer);
          break;
        case quoteItemAction.undelete:
          await this.internalUndeleteQuoteItem(quoteItemContainer);
          break;
        default:
          fireQuickWarningToast(LangQuoteView.unsupportedAction({ action: action.toString() }));
      }
    }
  );

  async tabDeletedEvent(e: TabCloseEvent): Promise<void> {
    if (e.page.data instanceof QuoteItemView) {
      e.newIndex = this.quoteItemsViewPageIndex;
    }
  }

  public async generateReportV2(_dealerReport: DealerReport): Promise<boolean> {
    throw new Error('Report Override not implemented.');
  }

  async afterShopDrawingFileCreated(_drawingFile: FileMap, _isTempFile: boolean) {
    //donothing
  }

  /**
   * The shared "is this quote fit to act on" gate. Two independent conditions can block, and each has its
   * own remedy, so each reports itself: stale pricing sends the user to Update Price, out-of-date items to
   * Validate. Pricing is checked first because it is the one a batch change causes, and because Validate
   * has nothing to report while it is the only thing outstanding.
   */
  async itemsNeedValidation(changesAllowed = false) {
    if (await this.blockedByStalePricing()) {
      return true;
    }

    const revalidationMsg = asMarkdown(
      LangQuoteView.issuedQuoteRevalidation({ supplierName: this.quoteContainerManager.supplierName })
    );
    const validationMsg = asMarkdown(
      LangQuoteView.activeQuoteValidation({ supplierName: this.quoteContainerManager.supplierName })
    );
    if (await this.mustValidateItems()) {
      await information(changesAllowed ? validationMsg : revalidationMsg, LangQuoteView.invalidQuoteItem());
      return true;
    }
    return false;
  }

  async canCompleteReview(): Promise<boolean> {
    return true;
  }

  addReportMenuItems(_childEvents: TabAction[]) {}

  addSupplierMenuItems(_childEvents: TabAction[]) {}

  async produceShopDrawing() {
    throw new Error('please overrride this method');
  }

  async produceSupplierQuote() {
    throw new Error('please overrride this method');
  }

  protected includeShippingLineItem(): boolean {
    return true;
  }

  protected async doAbandoned(): Promise<void> {
    if (this.isNew()) await this.quoteContainerManager.deleteQuote();
  }

  protected glassViewReadonly(): boolean {
    //if the logic changes here, then the reaonly alert on the v6-quote-supplier-glass-view.ts must also change
    return this.itemPagesOpenCount > 0;
  }

  protected getQuoteViewChildFactory(): QuoteViewChildFactory {
    return new QuoteViewChildFactoryImpl(this);
  }

  protected hasSupplierGlassView(): boolean {
    return false;
  }

  protected isNew(): boolean {
    return this.quoteContainer.isNewQuote;
  }

  protected async quoteItemViewFactory(
    quoteItemContainer: QuoteItemContainer | null,
    providerType: string,
    contentType: number,
    supplierSelector: unknown
  ): NullPromise<QuoteItemView> {
    if (providerType !== '') {
      const options = await this.getQuoteItemViewOptions(quoteItemContainer);
      return await constructAsync(
        this.quoteViewChildFactory.getSupplierQuoteItemView(contentType, options, supplierSelector)
      );
    } else if (providerType === '') {
      //these are the default non-provider line items
      switch (contentType) {
        case quoteItemContentType.shipping:
          return await constructAsync(new ShippingLineItemView(await this.getQuoteItemViewOptions(quoteItemContainer)));
        case quoteItemContentType.freehand:
          return await constructAsync(new FreehandLineItemView(await this.getQuoteItemViewOptions(quoteItemContainer)));
        case quoteItemContentType.specialItem:
          return await constructAsync(new SpecialLineItemView(await this.getQuoteItemViewOptions(quoteItemContainer)));
        case quoteItemContentType.dealerQuotePriceAdjustment:
          return null;
        default:
          if (isContentTypeSSI(contentType)) {
            return await constructAsync(
              new SpecialLineItemView(await this.getQuoteItemViewOptions(quoteItemContainer))
            );
          }
          await showDevelopmentError(`Content Type ${contentType} is not supported`);
          break;
      }
    } else await showDevelopmentError(`Provider Type "${providerType}" is not supported`);
    return null;
  }

  protected bodyTemplate(): EventTemplate {
    this.buildQuoteActionMenu();
    return super.bodyTemplate();
  }

  protected doAddPages(_pages: TabPage[]) {
    //override;
  }

  /**
   * inherited;
   * @returns
   */
  protected createTabManager(): TabManager {
    // build static pages for each of the configured table settings
    const getInitialPages = (): TabPage[] => {
      const pages: TabPage[] = [];
      pages.push(this.createDetailPage());
      pages.push(this.createSupplierQuoteDefaultsPage());
      if (this.hasSupplierGlassView()) pages.push(this.createSupplierGlassPage());
      pages.push(this.createQuoteItemSummaryPage());
      this.doAddPages(pages);
      return pages;
    };

    return new TabManager({
      initialIndex: 0,
      pages: () => getInitialPages(),
      events: {
        onTabClosed: async e => await this.tabDeletedEvent(e)
      }
    });
  }

  protected createSupplierQuoteDefaultsPage(): TabPage {
    return {
      caption: () => LangQuoteView.defaults(),
      canClose: () => false,
      canLeave: async () => await this.allowPageSwitch(),
      onEnter: async () => {
        await this.waitInit();
        await this.supplierQuoteDefaultsView.refresh();
      },
      content: () => {
        return this.supplierQuoteDefaultsView.ui;
      },
      secondaryButtons: () => {
        if (this.isLockedFromUse) return html``;
        return html` ${this.abandonAndCloseButton()} ${this.supplierQuoteDefaultsView.buttonMenu()}`;
      },
      hidden: () => {
        const qcm = this.quoteContainerManager;
        if (!qcm) return true;
        if (qcm.hasFrames) return false;
        return true;
      },
      data: this.supplierQuoteDefaultsView,
      fragment: 'supplierDefaults'
    };
  }

  protected createSupplierGlassPage(): TabPage {
    return {
      caption: () => LangQuoteView.quoteIguPlural(),
      canClose: () => false,
      canLeave: async () => await this.allowPageSwitch(),
      onEnter: async () => {
        await this.waitInit();
        if (!this.supplierGlassView) throw new DevelopmentError('supplierGlassView is null');

        //just display a message. we will make it readonly
        await this.checkAllFramesClosed();
      },
      content: () => {
        if (!this.supplierGlassView) throw new DevelopmentError('supplierGlassView is null');
        return this.supplierGlassView;
      },
      secondaryButtons: () => {
        if (this.isLockedFromUse) return html``;
        if (!this.supplierGlassView) throw new DevelopmentError('supplierGlassView is null');
        return html` ${this.abandonAndCloseButton()} ${this.supplierGlassView.buttonMenu()}`;
      },
      dispose: async () => {
        if (this.supplierGlassView) await this.supplierGlassView.releaseResources();
      },
      data: this.supplierGlassView,
      fragment: 'quoteIgu'
    };
  }

  protected async checkAllFramesClosed(): Promise<boolean> {
    if (this.itemPagesOpenCount > 0) {
      fireQuickWarningToast(LangQuoteView.closeAllQuoteItemBeforeEditingQuoteIgu());
      return false;
    }
    return true;
  }

  protected requiresSSIRecalculation(): boolean {
    return false;
  }

  protected async internalSaveData(): Promise<boolean> {
    let needsSSIProcessing = false;
    needsSSIProcessing =
      needsSSIProcessing ||
      (this.quoteContainerManager.quoteAddressChanged() && this.quoteContainerManager.isSSINeedsShippingAddress);

    needsSSIProcessing = needsSSIProcessing || this.requiresSSIRecalculation();
    if (needsSSIProcessing) {
      await this.quoteContainerManager.runAndSaveSSI();
      if (!this.quoteContainerManager.quoteChanged()) return true;
    }
    //this will populate the managed quote with data from the UI
    return await this.quoteContainerManager.saveQuote(isAutoSaving());
  }

  /**
   *
   * @returns inherited
   */
  protected getValidationErrors(): string[] {
    //prepare for save should always be called first
    return [
      ...this.detailView.getValidationErrors(),
      ...this.itemsView.getValidationErrors(),
      ...(this.supplierGlassView?.getValidationErrors() ?? [])
    ];
  }

  protected async quoteItemViewAlreadyOpen(): Promise<boolean> {
    const alreadyOpen = this.itemPagesOpenCount >= this.maxItemPageAllowed;
    if (alreadyOpen) await information(LangQuoteView.closeAndSaveExistingFrameOrSpecialItemEditor());
    return alreadyOpen;
  }

  protected async addNewQuoteItemFrame(frameSelector?: unknown): Promise<boolean> {
    if (this.isDataReadonly()) return false;
    if (!(await this.allowPageSwitch())) return false;
    if (!(await this.canAddItemFrame())) return false;

    await this.closeLastAvailableQuoteItemView();

    const providerType = await this.selectService();
    const quoteItemView = await this.quoteItemViewFactory(
      null,
      providerType,
      supplierQuoteItemContentType.CID_FRAM,
      frameSelector
    );
    try {
      if (quoteItemView) {
        await quoteItemView.prepareEditor();
        if (quoteItemView.readyToEdit) {
          await this.launchQuoteItemView(quoteItemView);
          return true;
        }
        return false;
      }
    } catch (e) {
      await showError(e as Error);
      return false;
    }

    return false;
  }

  protected async canAddItemFrame(): Promise<boolean> {
    return true;
  }

  protected async launchQuoteItemView(quoteItemView: QuoteItemView | null): Promise<void> {
    if (quoteItemView) {
      const page = quoteItemView.isTab() ? quoteItemView.createTabPage() : null;
      if (page) {
        page.onClose = async page => {
          if (page.data instanceof QuoteItemView) {
            this.lastDeletedTabItemId = (page.data as QuoteItemView).quoteItemContainer?.item.id;
          }
        };

        await this.closeLastAvailableQuoteItemView();

        if (await this.quoteItemViewAlreadyOpen()) return;
        this.tabManager.addPage(page);
      } else if (quoteItemView.hasModalEditDialog) {
        // a fallback for items that have a property dialog, but no page control.
        await quoteItemView.executeModalEditDialog();
      } else if (quoteItemView.hasPropertyDialog) {
        // a fallback for items that have a property dialog, but no page control.
        await quoteItemView.executePropertyDialog();
      } else fireQuickWarningToast(LangQuoteView.cannotEditQuoteItem());
    }
  }

  protected async selectService(): Promise<string> {
    return currentQuoteSupplierProvider;
  }

  /**
   * update the state and save the quote. this will work even if in readonly state.
   * @param state the new quote state.
   * @returns
   */
  protected async internalRevertQuoteState(state: QuoteState): Promise<boolean> {
    this.quoteContainerManager.quote.state = state;
    return await this.performAutoSave();
  }

  protected async canCreateShopDrawingPreview(): Promise<boolean> {
    return false;
  }

  protected async onQuoteStateChanged(_state: QuoteState): Promise<void> {
    //do nothing
  }

  protected getStandardButtons() {
    const stateEngine = this.getQuoteStateEngine();

    const allowSupplierReview =
      this.supplierQuoteConfig === undefined ? true : this.supplierQuoteConfig.allowSupplierReview;

    return {
      approve: {
        caption: () =>
          //if this is approving a quote that was already reviewed and approved by supplier
          //we are bypassing accepted state, but displaying accept on the button
          !allowSupplierReview
            ? LangQuoteView.accept()
            : this.quoteContainerManager.quote.supplierApproved
              ? LangQuoteView.accept()
              : LangQuoteView.approve(),
        onClick: async () => {
          if (await this.itemsNeedValidation()) {
            return;
          }
          const success = await this.setQuoteState(QuoteState.Approved);
          if (success) {
            await this.onQuoteStateChanged(QuoteState.Approved);
          }
        },

        disabled: stateEngine.isStateChangeBlocked(QuoteState.Approved)
      },

      review: {
        caption: () => LangQuoteView.editRevision(),
        onClick: async () => {
          if (await this.itemsNeedValidation()) {
            return;
          }

          const success = await this.setQuoteState(QuoteState.SupplierReviewPending);
          if (success) {
            await this.onQuoteStateChanged(QuoteState.SupplierReviewPending);
          }
        },

        disabled: stateEngine.isStateChangeBlocked(QuoteState.SupplierReviewPending)
      },
      reviewDone: {
        caption: () => LangQuoteView.completeRevision(),
        onClick: async () => {
          if (await this.itemsNeedValidation()) {
            return;
          }
          if (!(await this.canCompleteReview())) return;
          const success = await this.setQuoteState(QuoteState.SupplierReviewed);
          if (success) {
            await this.onQuoteStateChanged(QuoteState.SupplierReviewed);
          }
        },

        disabled: stateEngine.isStateChangeBlocked(QuoteState.SupplierReviewed)
      },
      cancel: {
        caption: () => LangQuoteView.cancel(),
        onClick: async () => {
          const success = await this.setQuoteState(QuoteState.Cancelled);
          if (success) {
            await this.onQuoteStateChanged(QuoteState.Cancelled);
          }
        },
        disabled: stateEngine.isStateChangeBlocked(QuoteState.Cancelled)
      },
      issue: {
        caption: () => LangQuoteView.issue(),
        onClick: async () => {
          if (await this.itemsNeedValidation(true)) {
            return;
          }

          const success = await this.setQuoteState(QuoteState.IssuePending);
          if (success) {
            await this.onQuoteStateChanged(QuoteState.IssuePending);
          }
        },
        disabled: stateEngine.isStateChangeBlocked(QuoteState.IssuePending)
      },
      activate: {
        caption: () => LangQuoteView.makeQuoteActive(),
        onClick: async () => {
          if (await this.itemsNeedValidation()) {
            return;
          }
          const success = await this.setQuoteState(QuoteState.Active);
          if (success) {
            await this.onQuoteStateChanged(QuoteState.Active);
          }
        },
        disabled: stateEngine.isStateChangeBlocked(QuoteState.Active)
      },
      accept: {
        caption: () => LangQuoteView.accept(),
        onClick: async () => {
          if (await this.itemsNeedValidation()) {
            return;
          }

          const success = await this.setQuoteState(QuoteState.Accepted);
          if (success) {
            await this.onQuoteStateChanged(QuoteState.Accepted);
          }
        },
        disabled: !allowSupplierReview || stateEngine.isStateChangeBlocked(QuoteState.Accepted)
      },
      reject: {
        caption: () => LangQuoteView.reject(),
        onClick: async () => {
          const success = await this.setQuoteState(QuoteState.Rejected);
          if (success) {
            await this.onQuoteStateChanged(QuoteState.Rejected);
          }
        },
        disabled: stateEngine.isStateChangeBlocked(QuoteState.Rejected)
      },
      addFrame: this.buildFrameButtons().map(x => {
        const btn: TabAction = {
          onClick: async () => {
            await this.addNewQuoteItemFrame(x ?? undefined);
          },
          caption: () => (x ? interpolateExternal(x.caption) : LangQuoteView.addFrame()),
          disabled: this.isDataReadonly()
        };
        return btn;
      }),
      addFreehand: {
        caption: () => LangQuoteView.addFreehand(),
        onClick: async () => {
          await this.addNewQuoteItemFreehand();
        },
        disabled: this.isDataReadonly()
      },
      addSpecialItem: this.buildSpecialItemButtons().map(x => {
        const btn: TabAction = {
          caption: () => (x ? interpolateExternal(x.caption) : LangQuoteView.requestSpecialItem()),
          onClick: async () => {
            await this.addNewQuoteItemSpecial(x ?? undefined);
          },
          disabled: this.isDataReadonly()
        };
        return btn;
      }),
      save: {
        onClick: async () => {
          if (this.isDataReadonly()) return;
          if (await this.saveActiveQuoteItem()) await this.performAutoSave();
        },
        caption: () => LangQuoteView.save(),
        disabled: this.isDataReadonly()
      },
      previewShopDrawing: {
        caption: () => LangQuoteView.shopDrawingPlural(),
        onClick: async () => {
          if (
            await AskConfirmation(
              LangQuoteView.notFinalDrawingContinue(),
              getConfirmationButtons()[ConfirmationButtonType.yesNo]
            )
          ) {
            if (await this.blockedByStalePricing()) return;
            if (await this.mustValidateItems()) {
              //we cannot produce a shop drawing if validations are required
              await information(LangQuoteView.shopdrawingsRevalidationRequired(), LangQuoteView.validationRequired());
              return;
            }
            if (this.quoteContainerManager.isV6) {
              await this.produceShopDrawing();
              fireQuickInformationToast(LangQuoteView.emailedLinkWhenReady());
            } else throw new DevelopmentError('createSupplierQuote not implemented');
          }
        }
      },
      createSupplierQuote: {
        caption: () => LangQuoteView.pushSupplierQuoteToV6(),

        onClick: async () => {
          if (await this.blockedByStalePricing()) return;
          if (await this.mustValidateItems()) {
            //we cannot produce a shop drawing if validations are required
            await information(LangQuoteView.supplierquoteRevalidationRequired(), LangQuoteView.validationRequired());
            return;
          }
          if (
            await AskConfirmation(
              LangQuoteView.createV6QuoteInSupplierSystem(),
              getConfirmationButtons()[ConfirmationButtonType.yesNo]
            )
          ) {
            if (this.quoteContainerManager.isV6) {
              await this.produceSupplierQuote();
            } else throw new DevelopmentError('createSupplierQuote not implemented');
          }
        }
      }
    };
  }

  protected buildFrameButtons(): (ButtonCaptionProvider | null)[] {
    return [null];
  }
  protected buildSpecialItemButtons(): (ButtonCaptionProvider | null)[] {
    return [null];
  }

  protected customButtons(_state: QuoteState): TabAction[] {
    return [];
  }

  /**
   * Tries to find an open QuoteItemView for the given quote item.
   * @param quoteItemContainer
   * @returns
   */
  protected findIfPageAvailable(quoteItemContainer: QuoteItemContainer): TabPage | undefined {
    return this.tabManager.pages.find(page => {
      if (page.data instanceof QuoteItemView) {
        if ((page.data as QuoteItemView).quoteItemContainer?.item.id === quoteItemContainer.item.id) {
          return true;
        }
      }
      return false;
    });
  }

  /**
   * Closes the last available QuoteItemView in the page control.
   * If a QuoteItemView is found, it is closed by calling the 'closePage' method of the page control.
   *
   * @returns {Promise<void>} - A Promise that resolves when the QuoteItemView is closed, or if no QuoteItemView is found.
   */
  protected async closeLastAvailableQuoteItemView(): Promise<void> {
    const frameEditor = this.tabManager.pages.reduceRight(
      (acc: TabPage | undefined, page) => (page.data instanceof QuoteItemView ? page : acc),
      undefined
    );

    if (frameEditor) {
      const id = (frameEditor.data as QuoteItemView).quoteItemContainer?.item.id;
      await this.tabManager.closePage(frameEditor);

      if (id) this.dispatchCustom('!wm-scroll-into-view', { selector: `div[data-rowid='${id}']` });
    }
  }

  /**
   * Find the last available Open QuoteItemView in the page control.
   */
  protected async findLastAvailableOpenQuoteItemView(): Promise<TabPage | undefined> {
    const frameEditor = this.tabManager.pages.reduceRight(
      (acc: TabPage | undefined, page) => (page.data instanceof QuoteItemView ? page : acc),
      undefined
    );

    if (frameEditor) {
      return frameEditor;
    }
    return undefined;
  }

  protected async closePageIfAvailable(quoteItemContainer: QuoteItemContainer): Promise<boolean> {
    const foundPage = this.findIfPageAvailable(quoteItemContainer);
    if (foundPage) {
      await this.tabManager.closePage(foundPage);

      if (quoteItemContainer)
        this.dispatchCustom('!wm-scroll-into-view', { selector: `div[data-rowid='${quoteItemContainer.item.id}']` });

      return true;
    }
    return false;
  }

  protected async internalDeleteQuoteItem(quoteItemContainer: QuoteItemContainer): Promise<void> {
    const qm = this.quoteContainerManager;
    await this.closePageIfAvailable(quoteItemContainer);

    //TODO when deleting an item, we are going to need to hook in the SSI processing
    if (await qm.deleteQuoteItem(quoteItemContainer)) {
      fireQuickSuccessToast(LangQuoteView.quoteItemDeleted());
    } else {
      fireQuickErrorToast(LangQuoteView.unableToDeleteSelectedQuoteItem());
    }
  }
  protected async internalUndeleteQuoteItem(quoteItemContainer: QuoteItemContainer): Promise<void> {
    const qm = this.quoteContainerManager;
    await this.closePageIfAvailable(quoteItemContainer);

    //TODO when undeleting an item, we are going to need to hook in the SSI processing
    if (await qm.undeleteQuoteItem(quoteItemContainer)) {
      fireQuickSuccessToast(LangQuoteView.quoteItemUndeleted());
    } else {
      fireQuickErrorToast(LangQuoteView.unableToUndeleteSelectedQuoteItem());
    }
  }

  protected async constructAndLaunchItemView(
    providerType: string,
    itemContentType: number,
    supplierSelector?: unknown
  ): Promise<boolean> {
    const quoteItemView = await this.quoteItemViewFactory(null, providerType, itemContentType, supplierSelector);
    try {
      if (quoteItemView) {
        await quoteItemView.prepareEditor();
        if (quoteItemView.readyToEdit) {
          await this.launchQuoteItemView(quoteItemView);
          return true;
        }
        return false;
      }
    } catch (e) {
      await showError(e as Error);
      return false;
    }
    return false;
  }

  protected async internalOpenQuoteItemPropertyDialog(quoteItemContainer: QuoteItemContainer): Promise<void> {
    if (await this.focusIfPageAvailable(quoteItemContainer)) {
      fireQuickWarningToast(LangQuoteView.saveAndCloseQuoteItemBeforeEditingProperties());
      return;
    }

    const quoteItemView = await this.internalCreateQuoteItemView(quoteItemContainer, false);
    if (quoteItemView) {
      if (quoteItemView.hasPropertyDialog) {
        // a fallback for items that have a property dialog, but no page control.
        await quoteItemView.executePropertyDialog();
      }
    }
  }

  private _eventQuote = (_data: unknown, _eventName: string) => {
    const data = _data as SSEQuote;
    const state = this.quoteContainerManager.quote.state;
    if (data.id !== this.quoteContainerManager.quote.id) return;
    const resourceUpdateRequired =
      (state === QuoteState.IssuePending && data.state !== QuoteState.IssuePending) ||
      (state === QuoteState.ResetInProgress && data.state !== QuoteState.ResetInProgress) ||
      (data.recordVersion !== this.quoteContainerManager.quote.recordVersion &&
        this.quoteContainerManager.isReadonly());
    if (resourceUpdateRequired)
      this.ui.dispatchEvent(
        new CustomEvent('resource-changed', {
          bubbles: true,
          composed: true,
          detail: {}
        })
      );
  };

  private abandonAndCloseButton() {
    if (this.isLockedFromUse || this.isDataReadonly()) return html``;
    return html` <webmodule-button @click=${() => this.abandonAndClose()} variant="default" size="small">
      ${this.discardChangesText}
    </webmodule-button>`;
  }

  /**
   * this is used to change state, and ensures that anything is saved that needs to be
   * saved on an active item that might be open before saving the quote
   * @param state
   * @returns
   */
  private async setQuoteState(state: QuoteState): Promise<boolean> {
    try {
      if (await this.saveActiveQuoteItem()) {
        await this.prepareForSave();
        return await this.quoteContainerManager.quoteStateChange(state);
      } else return false;
    } finally {
      this.requestUpdateAndPropagate();
    }
  }

  private buildQuoteActionMenu() {
    if (!this._tabManager) return;
    const actions: TabAction[] = [];
    const buttons = this.getStandardButtons();

    // Reports are unavailable while supplier pricing is deferred (Task 230006). Every figure a report prints
    // is derived from the same stale numbers the items table already refuses to total - so generating one
    // would produce a document that looks authoritative and is not, and PDFs get sent to clients. Disabled
    // rather than blocked on click: the whole point of the Deferred state is that the quote tells you it
    // cannot be trusted yet, and Update Price is right there on the Items toolbar as the remedy.
    //
    // Keyed on the raw ssiDeferred flag, not on the action-blocking predicate: accuracy does not depend on
    // whether the quote happens to be editable. A report off stale pricing is wrong either way.
    const reports: TabAction = {
      caption: () => LangQuoteView.reports(),
      disabled: this.quoteContainerManager.ssiDeferred,
      items: []
    };

    const supplierMenu: TabAction = {
      caption: () => LangQuoteView.supplier(),
      items: []
    };
    if (reports.items) this.addReportMenuItems(reports.items);
    if (supplierMenu.items) this.addSupplierMenuItems(supplierMenu.items);

    const status: TabAction = {
      caption: () => LangQuoteView.status(),
      items: []
    };
    const frameButton: TabAction =
      buttons.addFrame.length === 1
        ? buttons.addFrame[0]
        : {
            caption: () => LangQuoteView.addFrame(),
            disabled: this.isDataReadonly(),
            items: buttons.addFrame.filter(x => !x.disabled)
          };
    const specialItemButton: TabAction =
      buttons.addSpecialItem.length === 1
        ? buttons.addSpecialItem[0]
        : {
            caption: () => LangQuoteView.requestSpecialItem(),
            disabled: this.isDataReadonly(),
            items: buttons.addSpecialItem.filter(x => !x.disabled)
          };
    const pushValidStatusMenu = (...args: TabAction[]) => {
      status.items = args.filter(x => !x.disabled) ?? [];
      if (status.items?.length > 0) actions.push(status);
    };
    const pushButton = (...args: (TabAction | undefined | null)[]) => {
      if (this.isDataReadonly()) return;
      args.forEach(x => {
        if (x !== undefined && x !== null) actions.push(x);
      });
    };

    if (!this.isLockedFromUse)
      switch (this.quoteContainerManager.quote.state) {
        case QuoteState.Draft:
          pushValidStatusMenu(buttons.activate, buttons.cancel);
          pushButton(frameButton, buttons.addFreehand);
          break;
        case QuoteState.Active:
          pushValidStatusMenu(buttons.issue, buttons.cancel);
          pushButton(frameButton, buttons.addFreehand);

          if (this.supplierQuoteConfig?.allowCustomItemRequest && buttons.addSpecialItem.length > 0)
            pushButton(specialItemButton);

          pushButton(...this.customButtons(QuoteState.Active));

          break;
        case QuoteState.Issued:
          pushValidStatusMenu(buttons.approve, buttons.accept, buttons.reject);
          break;
        case QuoteState.Accepted:
        case QuoteState.Accepted_AssignedToReviewer:
          pushValidStatusMenu(buttons.approve, buttons.review, buttons.reject);
          break;
        case QuoteState.SupplierReviewPending:
          pushValidStatusMenu(buttons.reviewDone);
          pushButton(frameButton, buttons.addFreehand);

          if (this.supplierQuoteConfig?.allowCustomItemRequest && buttons.addSpecialItem.length > 0)
            pushButton(specialItemButton);

          pushButton(...this.customButtons(QuoteState.SupplierReviewPending));

          break;
        case QuoteState.SupplierReviewed:
          pushValidStatusMenu(buttons.issue, buttons.cancel);
          break;
      }

    if (reports.items && reports.items.length > 0) actions.push(reports);
    if (isSupplierAgent() || isDebugMode())
      if (supplierMenu.items && supplierMenu.items.length > 0) actions.push(supplierMenu);

    if (!isAutoSaving()) actions.push(buttons.save);

    this.tabManager.setActions(actions);
  }

  private async saveActiveQuoteItem(): Promise<boolean> {
    //if we are readonly, we never save anything, even if the UI accidentally things we should.
    if (!this.quoteContainerManager.isReadonly() && this.tabManager.activePage?.data instanceof QuoteItemView) {
      const view = this.tabManager.activePage?.data as QuoteItemView;
      if (await view.dataNeedsSaving()) return await view.saveQuoteItem();
      else return true;
    }
    return true;
  }

  private async internalCreateQuoteItemView(
    quoteItemContainer: QuoteItemContainer,
    needsPrepareEditor: boolean
  ): NullPromise<QuoteItemView> {
    const providerType = quoteItemContainer.item.serviceProvider;
    const quoteItemView = await this.quoteItemViewFactory(
      quoteItemContainer,
      providerType,
      quoteItemContainer.item.quoteItemContentType,
      undefined
    );
    if (quoteItemView)
      try {
        if (needsPrepareEditor) await quoteItemView.prepareEditor();
        if (!needsPrepareEditor || quoteItemView.readyToEdit) return quoteItemView;
      } catch {
        return null;
      }
    return null;
  }

  /**
   * this is the generic item edit launch routine that checks a number of things.
   * first it checks if there is a page tab open for this item, and focus it if available
   * next check is that we only allow a certain count of tabs to be open at any time
   * to restrict resources and focus work. if this limit is going to exceed we cancel
   * if the item is not a page control item, we check and lauch its modal editor if it has one,
   * finally if not, launch the property editor if it has one.
   * @param quoteItemContainer the item to open for editing
   * @returns
   */
  protected async internalOpenQuoteItemForEdit(quoteItemContainer: QuoteItemContainer): Promise<void> {
    if (await this.focusIfPageAvailable(quoteItemContainer)) return;
    if (
      (isFrame(quoteItemContainer.item) || isSpecialItem(quoteItemContainer.item)) &&
      (await this.quoteItemViewAlreadyOpen())
    )
      return;

    if (
      quoteItemContainer.price.supplierPriceAdjustmentEnabled &&
      quoteItemContainer.price.supplierPriceAdjustment != 0
    ) {
      const message = LangQuoteView.discardSupplierPriceAdjustmentOnEdit();
      if (
        !(await AskConfirmation(message, buttonsContinueCancel(), undefined, LangQuoteView.supplierPriceAdjustment()))
      )
        return;
    }

    const quoteItemView = await this.internalCreateQuoteItemView(quoteItemContainer, true);
    await this.launchQuoteItemView(quoteItemView);
  }

  /**
   * before we create and open a page item for editing something, we always want to test if it already exists
   * and focus it instead
   * @param quoteItemContainer
   * @returns
   */
  private async focusIfPageAvailable(quoteItemContainer: QuoteItemContainer): Promise<boolean> {
    const foundPage = this.findIfPageAvailable(quoteItemContainer);
    if (foundPage) {
      await this.tabManager.setActivePage(foundPage);
      return true;
    }
    return false;
  }

  private createDetailPage(): TabPage {
    return {
      caption: () => LangQuoteView.information(),
      canClose: () => false,
      canLeave: async () => await this.allowPageSwitch(),
      onEnter: async () => {
        await this.detailView.loadOrRefresh();
      },
      content: () => {
        return this.detailView.ui;
      },
      secondaryButtons: () => {
        if (this.isLockedFromUse) return html``;
        return html` ${this.abandonAndCloseButton()} ${this.detailView.buttonMenu()}`;
      },
      data: this.detailView,
      fragment: 'details'
    };
  }
}

@customElement('wm-quoteview')
export class QuoteView extends ModalViewBase {
  view: QuoteDataEntryView | null = null;
  options: QuoteViewOptions;

  constructor(options: QuoteViewOptions) {
    super();
    this.options = options;
  }

  /**
   * inherited
   * @returns
   */
  protected get modalSize() {
    return 'modal-fullscreen';
  }

  /**
   * inherited
   * @returns
   */
  async canClose(): Promise<boolean> {
    return (await this.view?.canClose()) ?? true;
  }

  async afterConstruction(): Promise<void> {
    this.view = await constructAsync(this.createView());
  }

  /**
   * inherited
   * @returns
   */
  protected getTitle(): Snippet {
    return this.view?.getTitle() ?? '';
  }

  protected createView(): QuoteDataEntryView {
    return new QuoteDataEntryView(this.options, this);
  }

  protected bodyTemplate(): TemplateResult {
    return html`${this.view?.ui}`;
  }
}
